import { getScreenplayV2S } from '@/service/local-db'
import { nanoid } from 'nanoid'
import { createContext, useContext, useEffect, useMemo, useState } from 'react'

import { getAudioDurationFromBlob } from '@/shared/lib/audio-utils'
import { db } from '@/shared/lib/db'
import { V2SElement, VoiceElement } from '@/pages/V2SPage'
import { useAuth } from '@/features/auth'
import { useWorkspace } from '@/features/workspace'

type V2SContext = {
    // screenplayV2S: ScreenplayV2S | null
    addedAudios: V2SElement[]
    totalDuration: number
    addVoiceElement: (element: VoiceElement) => void
    removeVoiceElement: (element: V2SElement) => void
    setLanguage: (language: string) => void
    language: string
}
const V2SContext = createContext<V2SContext | undefined>(undefined)

export const V2SContextProvider = ({ children }: { children: React.ReactNode }) => {
    const { project } = useWorkspace()
    const { user } = useAuth()

    const [addedAudios, setAddedAudios] = useState<V2SElement[]>([])

    const [language, setLanguage] = useState('en')

    const addVoiceElement = async (voiceElement: VoiceElement) => {
        const duration = await getAudioDurationFromBlob(voiceElement.audioBlob)

        const meta = {
            id: crypto.randomUUID(), // Generate UUID for local DB
            ...voiceElement,
            created_at: new Date(),
            updated_at: new Date(),
            userId: user!.uid,
            projectId: project!.id,

            duration,
        }
        await db.v2SElements.add(meta)
        setAddedAudios((prev) => [...prev, meta])
    }
    const removeVoiceElement = async (voiceElement: V2SElement) => {
        console.log('Deleting one record with uid:', voiceElement)
        const record = await db.v2SElements.where('id').equals(voiceElement.id).first()

        if (record?.id != null) {
            await db.v2SElements.delete(record.id)
        }

        setAddedAudios((prev) => {
            const indexToRemove = prev.findIndex((el) => el.id === voiceElement.id)
            if (indexToRemove === -1) return prev // no match found
            return prev.filter((_, i) => i !== indexToRemove)
        })
    }
    useEffect(() => {
        async function fetchV2S() {
            const v2s = await getScreenplayV2S(project!.id)
            setAddedAudios(v2s || [])
        }
        if (project) fetchV2S()
    }, [project])
    const totalDuration = useMemo(
        () => addedAudios.reduce((sum, el) => sum + (el.duration ?? 0), 0),
        [addedAudios]
    )
    return (
        <V2SContext.Provider
            value={{
                addedAudios,
                addVoiceElement,
                removeVoiceElement,
                totalDuration,
                language,
                setLanguage,
            }}
        >
            {children}
        </V2SContext.Provider>
    )
}

export const useV2SContext = () => {
    const context = useContext(V2SContext)
    if (!context)
        throw new Error(
            'useScreenplayV2SContext must be used within a ScreenplayV2SContextProvider'
        )
    return context
}
